import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Star, Heart, MessageCircle, Globe, Sparkles, CheckCircle2, CalendarClock } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { useStartChat } from "../hooks/useStartChat";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const STATUS = {
  online: { label: "Disponible maintenant", dot: "#10B981" },
  busy: { label: "En consultation", dot: "#F59E0B" },
  offline: { label: "Actuellement absent", dot: "#9CA3AF" },
};

export default function ExpertProfile() {
  const { id } = useParams();
  const [expert, setExpert] = useState(null);
  const [fav, setFav] = useState(false);
  const [slots, setSlots] = useState([]);
  const startChat = useStartChat();
  const { user } = useAuth();

  const loadSlots = () => api.expertSlots(id).then(({ data }) => setSlots(data)).catch(() => {});
  useEffect(() => {
    api.getExpert(id).then(({ data }) => setExpert(data)).catch(() => {});
    loadSlots();
    if (user) api.favorites().then(({ data }) => setFav(data.some((f) => f.id === id))).catch(() => {});
  }, [id, user]);

  const book = async (slot) => {
    if (!user) { toast.error("Connectez-vous pour réserver."); return; }
    try { await api.book(expert.id, slot.id); toast.success("Rendez-vous réservé ! Un e-mail de confirmation vous a été envoyé."); loadSlots(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleFav = async () => {
    if (!user) { toast.error("Connectez-vous pour ajouter aux favoris."); return; }
    try {
      if (fav) { await api.removeFavorite(expert.user_id); setFav(false); }
      else { await api.addFavorite(expert.user_id); setFav(true); toast.success("Ajouté aux favoris."); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!expert) return <div className="py-24 text-center text-celeste-lilas/70">Chargement du profil…</div>;
  const st = STATUS[expert.presence] || STATUS.offline;

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
      <div className="grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-1">
          <div className="glass overflow-hidden rounded-3xl border border-celeste-or/15">
            <img src={expert.photo_url} alt={expert.professional_name} className="h-72 w-full object-cover" />
            <div className="p-5">
              {expert.is_demo && (
                <p className="mb-2 rounded-lg bg-celeste-night/70 px-2 py-1 text-center text-[11px] text-celeste-or">
                  {expert.demo_label}
                </p>
              )}
              <div className="flex items-center gap-2 text-sm">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: st.dot }} />
                <span className="text-celeste-lilas">{st.label}</span>
              </div>
              <div className="mt-4 flex gap-2">
                <Button data-testid="profile-start-chat" onClick={() => startChat(expert)} disabled={expert.presence !== "online"}
                  className="flex-1 gold-btn font-semibold disabled:opacity-40">
                  <MessageCircle className="mr-1.5 h-4 w-4" /> Démarrer le chat
                </Button>
                <Button data-testid="profile-fav-btn" variant="ghost" onClick={toggleFav}
                  className="border border-celeste-violet/30 text-celeste-lilas hover:bg-celeste-violet/15">
                  <Heart className={`h-4 w-4 ${fav ? "fill-red-400 text-red-400" : ""}`} />
                </Button>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs text-celeste-lilas/70">
                <div><div className="text-lg font-semibold text-celeste-or">{expert.rating?.toFixed(1)}</div>note</div>
                <div><div className="text-lg font-semibold text-celeste-or">{expert.reviews_count}</div>avis</div>
                <div><div className="text-lg font-semibold text-celeste-or">{expert.price_per_min}</div>cr/min</div>
              </div>
            </div>
          </div>

          <div className="glass mt-6 rounded-3xl p-5">
            <h3 className="mb-3 flex items-center gap-2 font-serif text-lg text-celeste-ivoire"><CalendarClock className="h-4 w-4 text-celeste-or" /> Réserver un rendez-vous</h3>
            {slots.length === 0 ? (
              <p className="text-sm text-celeste-lilas/75">Aucun créneau publié pour le moment. Suivez ce praticien pour être informé de ses prochaines disponibilités.</p>
            ) : (
              <div className="space-y-2" data-testid="booking-slots">
                {slots.map((s) => (
                  <div key={s.id} className="flex items-center justify-between rounded-xl border border-celeste-violet/20 bg-celeste-night/40 px-3 py-2">
                    <span className="text-sm text-celeste-lilas">{new Date(s.datetime).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" })}</span>
                    <Button data-testid={`book-slot-${s.id}`} size="sm" className="gold-btn font-semibold" onClick={() => book(s)}>Réserver</Button>
                  </div>
                ))}
              </div>
            )}
            <Button data-testid="profile-remind-btn" variant="ghost" onClick={toggleFav} className="mt-3 w-full border border-celeste-violet/30 text-celeste-lilas hover:bg-celeste-violet/15">
              {fav ? "Retiré des favoris" : "Suivre ce praticien"}
            </Button>
          </div>
        </div>

        <div className="lg:col-span-2">
          <h1 className="font-serif text-3xl text-celeste-ivoire sm:text-4xl">{expert.professional_name}</h1>
          <p className="mt-1 text-celeste-or">{expert.title}</p>

          <div className="mt-4 flex flex-wrap gap-2">
            {expert.specialties.map((s) => (
              <span key={s} className="rounded-full border border-celeste-violet/30 bg-celeste-violet/15 px-3 py-1 text-xs text-celeste-lilas">{s}</span>
            ))}
          </div>

          <div className="glass mt-6 rounded-3xl p-6">
            <h3 className="flex items-center gap-2 font-serif text-lg text-celeste-ivoire"><Sparkles className="h-4 w-4 text-celeste-or" /> Présentation</h3>
            <p className="mt-3 text-sm leading-relaxed text-celeste-lilas/85">{expert.bio}</p>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <div>
                <h4 className="text-sm font-semibold text-celeste-or">Méthodes</h4>
                <ul className="mt-2 space-y-1 text-sm text-celeste-lilas/80">
                  {expert.methods.map((m) => <li key={m} className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-celeste-or" /> {m}</li>)}
                </ul>
              </div>
              <div>
                <h4 className="flex items-center gap-1.5 text-sm font-semibold text-celeste-or"><Globe className="h-3.5 w-3.5" /> Langues</h4>
                <p className="mt-2 text-sm text-celeste-lilas/80">{expert.languages.join(", ")}</p>
              </div>
            </div>
          </div>

          <div className="glass mt-6 rounded-3xl p-6">
            <h3 className="flex items-center gap-2 font-serif text-lg text-celeste-ivoire"><Star className="h-4 w-4 text-celeste-or" /> Avis vérifiés</h3>
            <div className="mt-4 space-y-4">
              {(expert.reviews || []).length === 0 && <p className="text-sm text-celeste-lilas/60">Aucun avis pour le moment.</p>}
              {(expert.reviews || []).map((r) => (
                <div key={r.id} className="rounded-2xl border border-celeste-violet/20 bg-celeste-night/40 p-4" data-testid={`review-${r.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star key={i} className={`h-3.5 w-3.5 ${i < r.rating ? "fill-celeste-or text-celeste-or" : "text-celeste-lilas/30"}`} />
                      ))}
                    </div>
                    {r.verified && <span className="inline-flex items-center gap-1 text-[11px] text-emerald-300"><CheckCircle2 className="h-3 w-3" /> Consultation vérifiée</span>}
                  </div>
                  <p className="mt-2 text-sm text-celeste-lilas/85">{r.comment}</p>
                  <p className="mt-2 text-xs text-celeste-lilas/50">— {r.consultant_name || "Client"}</p>
                  {r.is_demo && <p className="mt-1 text-[10px] text-celeste-or/80">{r.demo_label}</p>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
